/* ============================================================================
   olmia-product.js — PDP interactivity (ported from design product.js / ur15.js).
   Drives: gallery (arrows + thumbs), variant pickers with live price deltas,
   variant -> real Shopify variant resolution (price + hidden id + availability),
   "Lees meer" description toggle, add-to-cart (intercepts {% form 'product' %} and
   calls OlmiaCart.add -> toast + drawer), sticky buy bar on scroll.
   Prices are painted by OlmiaUI.paintPrices per the shared PRICE RULE; every
   amount this file composes itself (de meerprijs-delta op een variantknop) loopt
   door dezelfde OlmiaUI.Money. Er wordt in HELE CENTEN gerekend — de config-JSON
   levert centen, dus de omweg via euro's blijft zo kort mogelijk.
   Reads the embedded [data-product-config] JSON (product.variants + base + cust).

   Editor-safe (see olmia-editor.js): init(root) re-parses the section's config into
   module state and rebinds the fresh gallery/variant/buy nodes; the delegated .vopt
   click listener is bound ONCE at module scope (reads current module state), and the
   sticky-bar window scroll listener is held at module scope and removed in teardown
   so a PDP re-render never leaves a stale scroll handler.
   ========================================================================== */
(function () {
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  /* PRICE RULE: nooit een eigen Intl.NumberFormat — de enige geldformatter van
     het thema staat in olmia-ui.js (OlmiaUI.Money). */
  const fromCents = (c) => OlmiaUI.Money.fromCents(c);
  const toCents = (eur) => OlmiaUI.Money.cents(eur);

  // ---- module state (reassigned on every init so the once-bound handlers below
  //      always act on the currently-rendered product) ----
  let scopeEl = document;
  let variants = [];
  let base = 0;
  let custBase = null;
  let firstVals = [];
  let sel = [];
  let stickyScroll = null;
  let voptBound = false;

  const variantFor = (opts) => variants.find(v =>
    (v.options || []).length === opts.length && v.options.every((val, i) => val === opts[i]));
  const current = () => variantFor(sel) || variants[0];
  // variant label for the bundle summary: drop the neutral defaults (design ur15.js)
  const variantLabel = () => sel.filter(v => v && v !== 'Standaard' && v !== 'Default Title').join(' · ');

  /* ---------- price delta labels on the option buttons ---------- */
  function annotateDeltas() {
    $$('.vopt', scopeEl).forEach(btn => {
      const gi = +btn.dataset.gi;
      const opts = firstVals.slice();
      opts[gi] = btn.dataset.value;
      const v = variantFor(opts);
      const delta = v ? (v.price - base) : 0;
      btn.querySelector('.d')?.remove();
      if (delta > 0) {
        const s = document.createElement('span');
        s.className = 'd';
        // `delta` is al in centen (v.price komt uit de Shopify-config-JSON);
        // rechtstreeks formatteren voorkomt een drijvende-komma-omweg.
        s.textContent = '+ ' + fromCents(delta);
        btn.appendChild(document.createTextNode(' '));
        btn.appendChild(s);
      }
    });
  }

  /* ---------- price + availability + hidden id ---------- */
  function setAvailability(available) {
    $$('[data-pdp-add]', scopeEl).forEach(btn => {
      btn.disabled = !available;
      const lbl = btn.querySelector('[data-add-label]');
      if (lbl) lbl.textContent = available ? (btn.dataset.labelAvail || T('product.add_to_cart', 'In winkelwagen')) : (btn.dataset.labelSold || T('product.sold_out', 'Uitverkocht'));
    });
  }

  function refreshPrice() {
    const v = current();
    if (!v) return;
    // In centen rekenen, pas op het laatst naar euro's: `custBase + delta/100`
    // gaf bij een klantprijs mét centen waarden als 233.79999999999998 in het
    // data-cust-attribuut.
    const priceEur = v.price / 100;
    const custEur = (custBase != null) ? (toCents(custBase) + (v.price - base)) / 100 : null;
    $$('[data-pdp-price]', scopeEl).forEach(el => {
      el.dataset.price = priceEur;
      if (custEur != null) el.dataset.cust = custEur; else el.removeAttribute('data-cust');
    });
    if (window.OlmiaUI) OlmiaUI.paintPrices(document);
    const idInput = $('[data-variant-id]', scopeEl);
    if (idInput) idInput.value = v.id;
    setAvailability(!!v.available);
    window.__olmiaPdpPriceEur = priceEur;
    document.dispatchEvent(new CustomEvent('olmia:price', { detail: { eur: priceEur, cust: custEur, id: v.id, label: variantLabel() } }));
  }

  /* ---------- variant selection ---------- */
  function selectValue(gi, val) {
    sel[gi] = val;
    const grp = $('.variant-group[data-group="' + gi + '"]', scopeEl);
    if (grp) {
      grp.querySelectorAll('.vopt').forEach(b => b.classList.toggle('active', b.dataset.value === val));
      const s = grp.querySelector('.sel');
      if (s) s.textContent = val;
    }
    refreshPrice();
  }

  function initVariants() {
    $$('.variant-group', scopeEl).forEach(grp => {
      const gi = +grp.dataset.group;
      grp.querySelectorAll('.vopt').forEach(b => b.classList.toggle('active', b.dataset.value === sel[gi]));
      const s = grp.querySelector('.sel');
      if (s) s.textContent = sel[gi];
    });
    annotateDeltas();
    // Delegated .vopt click bound ONCE (module scope): a re-render must not add a
    // second listener that would double-fire selectValue.
    if (!voptBound) {
      voptBound = true;
      document.addEventListener('click', (e) => {
        const v = e.target.closest('.vopt');
        if (!v) return;
        selectValue(+v.dataset.gi, v.dataset.value);
      });
    }
  }

  /* ---------- gallery (arrows + thumbs) ---------- */
  function initGallery() {
    const gal = $('.gallery', scopeEl);
    if (!gal) return;
    const main = $('#galMain', gal);
    const thumbs = $$('.gthumb', gal);
    const images = thumbs.length ? thumbs.map(t => t.dataset.src) : (main ? [main.getAttribute('src')] : []);
    let idx = 0;
    const show = (i) => {
      if (!images.length) return;
      idx = (i + images.length) % images.length;
      if (main) {
        // The server-rendered main image carries a responsive srcset (from
        // image_tag). A srcset always wins over src, so setting .src alone would
        // NOT swap the displayed photo. Drop srcset/sizes so the chosen thumbnail
        // (a full-width image_url) actually paints.
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = images[idx];
      }
      thumbs.forEach(t => t.classList.toggle('active', +t.dataset.i === idx));
    };
    gal.addEventListener('click', (e) => {
      const arrow = e.target.closest('[data-gal]');
      if (arrow) { show(idx + (+arrow.dataset.gal)); return; }
      const t = e.target.closest('.gthumb');
      if (t) show(+t.dataset.i);
    });
  }

  /* ---------- description "Lees meer" (cap ~300 chars, as design) ---------- */
  function initDesc() {
    const el = $('[data-desc]', scopeEl);
    if (!el) return;
    const full = el.textContent.trim();
    const MAX = 300;
    if (full.length <= MAX) return;
    const short = full.slice(0, MAX).replace(/\s+\S*$/, '');
    el.dataset.full = full;
    el.innerHTML = short + '… <button type="button" class="link-more" data-desc-more>' + T('product.read_more', 'Lees meer') + '</button>';
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-desc-more]')) el.textContent = el.dataset.full;
    });
  }

  /* ---------- add to cart ---------- */
  const qtyVal = () => { const q = $('[data-pdp-qty]', scopeEl); return Math.max(1, (+(q && q.value) || 1)); };
  function addToCart() {
    const v = current();
    if (!v || !v.available) return;
    if (window.OlmiaCart) OlmiaCart.add(v.id, qtyVal());
  }
  function initBuy() {
    const form = $('[data-product-form]', scopeEl);
    if (form) form.addEventListener('submit', (e) => { e.preventDefault(); addToCart(); });
    $('#stickyAdd', scopeEl)?.addEventListener('click', (e) => { e.preventDefault(); addToCart(); });
  }

  /* ---------- sticky buy bar ---------- */
  function initSticky() {
    const bar = $('#stickyBuy', scopeEl) || document.getElementById('stickyBuy');
    const anchor = $('.pdp__buy [data-pdp-add]', scopeEl) || $('[data-pdp-add]', scopeEl);
    if (!bar || !anchor) return;
    if (stickyScroll) window.removeEventListener('scroll', stickyScroll);
    stickyScroll = () => bar.classList.toggle('show', anchor.getBoundingClientRect().bottom < 0);
    window.addEventListener('scroll', stickyScroll, { passive: true });
    stickyScroll();
  }

  function teardown() {
    if (stickyScroll) { window.removeEventListener('scroll', stickyScroll); stickyScroll = null; }
  }

  function init(sectionRoot) {
    const scope = sectionRoot || document;
    const cfgEl = scope.querySelector('[data-product-config]');
    if (!cfgEl) return;
    let cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    const vs = cfg.variants || [];
    if (!vs.length) return;

    scopeEl = scope;
    variants = vs;
    base = variants[0].price;                    // cents (first variant = base, per design)
    custBase = (cfg.cust != null) ? +cfg.cust : null; // euros or null
    firstVals = (variants[0].options || []).slice();
    sel = firstVals.slice();                     // selected value per option index

    initGallery();
    initVariants();
    initDesc();
    initBuy();
    initSticky();
    refreshPrice();
  }

  if (window.OlmiaEditor) OlmiaEditor.register({
    key: 'olmia-product',
    match: (r) => !!(r && r.querySelector && r.querySelector('[data-product-config]')),
    init, teardown
  });

  /* WACHTEN OP OlmiaUI (PRICE RULE, 2026-08-21).
     layout/theme.liquid laadt olmia-ui.js NA {{ content_for_layout }}, dus dit
     sectie-script draait er normaal vóór — bij een defer-script is readyState op
     dat moment al 'interactive', zodat de oude regel hier meteen init() deed en
     OlmiaUI.Money nog niet bestond. Alle defer-scripts zijn klaar op
     DOMContentLoaded; daarop wachten kost niets en is exact het patroon dat
     assets/olmia-cart-page.js al gebruikte. */
  if (window.OlmiaUI) init(document);
  else if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else init(document);
})();
